import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import ReturnConfirmationCode from './ReturnConfirmationCode';

import AccountBreadcrumbs from 'components/account/AccountBreadcrumbs';
import DoneSection from 'components/account/Returns/DoneSection';
import ReturnConfirmationTitle from 'components/account/Returns/ReturnConfirmationTitle';
import ReturnConfirmationInstruction from 'components/account/Returns/ReturnConfirmationInstruction';
import ZawInfoBanner from 'components/account/Returns/ZawInfoBanner';
import { getReturnLabelImageObjectUrl } from 'apis/mafia';
import { logoutAsCustomer } from 'actions/account/account';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import { track } from 'apis/amethyst';
import { pvReturnConfirmation } from 'events/returns';
import { returnToAccountPath } from 'constants/appConstants';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/containers/returns/returns.scss';

export const ReturnStepConfirmation = ({
  marketplace,
  rmaId,
  UPSPickUpAddress,
  labelDelivery,
  isLabelless,
  zawSubsidyReissueEligibility,
  isZAWReturnsEnabled,
  breadcrumbsLinks,
  makeWhiteLabelRefundSummary,
  makeRefundSummary,
  ...props
}) => {
  const { account, returns, keepExchangeOrdersFromReturnCancel, fetchReturnLabelInfo, showPackagelessReturn } = props;
  const {
    boxInfo,
    postReturnInfo,
    labelInfo: { qrcodeUrl }
  } = returns;
  const { customerInfo } = account;
  const {
    account: { returnLabelUrl }
  } = marketplace;
  const {
    isPrivateLabel,
    account: { hideReturnsAndExchangesOnReturnConfirmation },
    returns: { showLabellessReturns, showReturnInstructionsBoxes, showPackagelessInVertical }
  } = marketplace;
  const optionsWithQRCode = [PROVIDE_LABEL_OPTIONS.LABELLESS, PROVIDE_LABEL_OPTIONS.UPS_PACKAGELESS, PROVIDE_LABEL_OPTIONS.WFM_PACKAGELESS];
  const hasQRCode = optionsWithQRCode.includes(labelDelivery);
  const warningLabelDelivryOptions = [PROVIDE_LABEL_OPTIONS.LABELLESS, PROVIDE_LABEL_OPTIONS.PICKUP];
  const isLabelDeliveryUPS = warningLabelDelivryOptions.includes(labelDelivery);

  useEffect(() => {
    const returnTo = returnToAccountPath;
    postReturnInfo.forEach(({ contractId, rmaId }) => {
      if (contractId || rmaId) {
        fetchReturnLabelInfo({ rmaId, contractId }, undefined, undefined, {}, getReturnLabelImageObjectUrl, returnTo, logoutAsCustomer);
      }
    });
  }, [postReturnInfo, fetchReturnLabelInfo]);

  useEffect(() => {
    if (postReturnInfo.length > 0) {
      track(() => [pvReturnConfirmation, { returnLabel: labelDelivery }]);
    }
  }, [postReturnInfo, labelDelivery]);

  return (
    <div className={css.doneContainer}>
      <AccountBreadcrumbs links={breadcrumbsLinks} currentText="Returns" />
      <ZawInfoBanner enabled={isZAWReturnsEnabled} zawSubsidyReissueEligibility={zawSubsidyReissueEligibility} confirmation={true} />
      <ReturnConfirmationTitle
        className={css.returnConfirmation}
        isPrivateLabel={isPrivateLabel}
        showReturnInstructionsBoxes={showReturnInstructionsBoxes}
        labelDelivery={labelDelivery}
        boxInfo={boxInfo}
        rmaId={rmaId}
        postReturnInfo={postReturnInfo}
        isLabelless={isLabelless}
      />
      {isLabelDeliveryUPS ? (
        <div className="lg:text-md mx-4 mb-5 mt-0 rounded-lg border border-l-8 border-warning bg-warning px-5 py-4 lg:mx-8">
          <p>
            <strong>This return code is intended for a single box.</strong> If you have multiple boxes,{' '}
            <Link to="/account" className="text-accent zappos:text-gray-900 zappos:underline zappos:hover:decoration-2">
              please cancel
            </Link>{' '}
            and initiate 2 separate returns codes.
          </p>
        </div>
      ) : (
        <h2 className={css.returnMethodTitle}>Return Method</h2>
      )}
      <div className={css.returnMethodWrapper}>
        <ReturnConfirmationInstruction
          showReturnInstructionsBoxes={showReturnInstructionsBoxes}
          labelDelivery={labelDelivery}
          customerInfo={customerInfo}
          boxInfo={boxInfo}
          postReturnInfo={postReturnInfo}
          UPSPickUpAddress={UPSPickUpAddress}
          showLabellessReturns={showLabellessReturns}
          showPackagelessReturn={showPackagelessInVertical && showPackagelessReturn}
        />
        {hasQRCode && (
          <div className={css.returnConfirmationCodeWrapper}>
            <ReturnConfirmationCode
              email={customerInfo?.email}
              labelDelivery={labelDelivery}
              qrcodeUrl={qrcodeUrl}
              returnLabelUrl={returnLabelUrl}
              rmaId={rmaId}
            />
          </div>
        )}
      </div>

      {!hideReturnsAndExchangesOnReturnConfirmation && (
        <div className={css.container}>
          {isPrivateLabel
            ? makeWhiteLabelRefundSummary({
                useCustomerReturnableFlag: keepExchangeOrdersFromReturnCancel,
                isConfirmationOrder: true,
                rmaId
              })
            : makeRefundSummary({ useCustomerReturnableFlag: keepExchangeOrdersFromReturnCancel, isConfirmation: true })}
          {isPrivateLabel && <DoneSection postReturnInfo={postReturnInfo} isLabelless={isLabelless} boxInfo={boxInfo} isButtonOnly={true} />}
        </div>
      )}
    </div>
  );
};
